// Turns the voice-led chords into drawn note-stacks for a second staff below
// the melody, so the "plan chord motions" result reads as real notation
// aligned under the melody it supports. Default clef is the guitar convention:
// treble sounding an octave lower ('treble8vb', written = sounding + 12),
// which puts drop-2 voicings on the staff where a guitarist reads them.
// See SPEC.md §7 and task #12.

import { activeStringsForFingering, frettedMidi } from '@/music/tuning';
import type { Fingering } from '@/music/voicing';
import { ledgerLineOffsets, spelledNote, staffOffset, type Clef } from './pitch';
import { accidentalColumns } from './accidentals';
import {
  ENGRAVING,
  SMUFL,
  accidentalGlyph,
  glyphHorizontalBounds,
  ledgerHalfWidth,
  type SmuflGlyph,
} from './smufl';

const TOP_LINE_OFFSET = 4; // staff-spaces from bottom to top line (5-line staff)

export interface DrawnVoicingNote {
  y: number; // staff-spaces below the top line
  /** Rightward shift in staff-spaces — the upper note of a second moves aside
   * so adjacent noteheads read as a cluster instead of printing overlapped. */
  dx?: number;
  accidental?: SmuflGlyph;
  /** Accidental centre relative to the chord's shared onset, in staff spaces. */
  accidentalDx?: number;
  ledger: number[];
}
export interface DrawnVoicing {
  barIndex: number;
  beat: number;
  notes: DrawnVoicingNote[]; // low -> high
}

/** Actual sounding MIDI pitches of a fretted voicing, low to high. */
export function fingeringMidis(
  fingering: Pick<Fingering, 'frets' | 'stringSet' | 'stringLayout' | 'voicingFamily'>,
): number[] {
  const midis: number[] = [];
  for (const str of activeStringsForFingering(fingering)) {
    const fret = fingering.frets[str];
    if (fret !== null && fret !== undefined) midis.push(frettedMidi(str, fret));
  }
  return midis.sort((a, b) => a - b);
}

export interface VoicedInput {
  barIndex: number;
  beat: number;
  midis: number[];
}

/** Clef for the voicing staff; 'treble8vb' writes an octave above sounding. */
export type VoicingClef = Clef | 'treble8vb';

/** Place each voiced chord's pitches on a staff (guitar treble-8vb by default). */
export function drawVoicings(
  voiced: VoicedInput[],
  opts: { clef?: VoicingClef; key?: string } = {},
): DrawnVoicing[] {
  const requested = opts.clef ?? 'treble8vb';
  const clef: Clef = requested === 'treble8vb' ? 'treble' : requested;
  const shift = requested === 'treble8vb' ? 12 : 0;
  return voiced.map((v) => {
    const notes: DrawnVoicingNote[] = v.midis.map((sounding) => {
      const midi = sounding + shift;
      const offset = staffOffset(midi, { key: opts.key, clef });
      const { accidental } = spelledNote(midi, { key: opts.key, clef });
      return {
        y: TOP_LINE_OFFSET - offset,
        accidental: accidental ? accidentalGlyph(accidental) : undefined,
        ledger: ledgerLineOffsets(offset).map((o) => TOP_LINE_OFFSET - o),
      };
    });
    // Seconds rule: adjacent (or unison) noteheads can't share a column — the
    // upper one steps right, zigzagging up a cluster (offset only when the
    // note below sits in the home column).
    for (let i = 1; i < notes.length; i++) {
      const below = notes[i - 1];
      const isSecond = below.y - notes[i].y < 1; // < a third apart
      if (isSecond && !below.dx) notes[i].dx = ENGRAVING.noteheadWholeWidth;
    }
    const altered = notes.filter((n) => n.accidental != null);
    if (altered.length) {
      const headLeft = Math.min(
        ...notes.map(
          (n) =>
            (n.dx ?? 0) +
            (n.ledger.length
              ? -ledgerHalfWidth(SMUFL.noteheadWhole)
              : glyphHorizontalBounds(SMUFL.noteheadWhole).left),
        ),
      );
      const { placements } = accidentalColumns(
        altered.map((n) => ({ glyph: n.accidental!, y: n.y, scale: 1 })),
        headLeft - 0.3,
      );
      altered.forEach((n, i) => {
        if (placements[i].duplicate) n.accidental = undefined;
        else n.accidentalDx = placements[i].x;
      });
    }
    return { barIndex: v.barIndex, beat: v.beat, notes };
  });
}

/** Visible horizontal extent of the generated chord, relative to its onset. */
export function voicingBounds(v: DrawnVoicing): { left: number; right: number } {
  const head = glyphHorizontalBounds(SMUFL.noteheadWhole);
  let left = 0,
    right = 0;
  for (const n of v.notes) {
    left = Math.min(left, (n.dx ?? 0) + head.left);
    right = Math.max(right, (n.dx ?? 0) + head.right);
    if (n.ledger.length) {
      left = Math.min(left, (n.dx ?? 0) - ledgerHalfWidth(SMUFL.noteheadWhole));
      right = Math.max(right, (n.dx ?? 0) + ledgerHalfWidth(SMUFL.noteheadWhole));
    }
    if (n.accidental != null) {
      const bounds = glyphHorizontalBounds(n.accidental);
      left = Math.min(left, (n.accidentalDx ?? -1) + bounds.left);
      right = Math.max(right, (n.accidentalDx ?? -1) + bounds.right);
    }
  }
  return { left, right };
}
